import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Sparkle, CircleNotch, Globe, CheckCircle, Check, Info, Plus, Trash, Link as LinkIcon, ShoppingCart } from '@phosphor-icons/react';
import { Button, TextInput, Title, Text, ThemeIcon, Paper, Box, Group, Stack, Alert, ActionIcon, SegmentedControl, Textarea, MultiSelect, Select, Badge } from '@mantine/core';
import { COUNTRY_OPTIONS, COUNTRY_GROUPS } from '../data/countries';
import api from '../utils/api';
import { getGoogleAccounts } from '../utils/googleAccounts';
import CampaignDraftReview from './CampaignDraftReview';

export default function CreateCampaignWizard({ onClose, onCreated, user }) {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  // Platform selection (shown if 2+ platforms connected)
  const [platform, setPlatform] = useState('');
  // Step 0: campaign type selection
  const [campaignType, setCampaignType] = useState(''); // 'single' | 'multi' | 'referral' | 'shopping'
  const [campaignGoal, setCampaignGoal] = useState('traffic'); // 'traffic' | 'leads'
  // Shopping-specific
  const [merchantCenters, setMerchantCenters] = useState([]);
  const [selectedMerchant, setSelectedMerchant] = useState('');
  const [shoppingName, setShoppingName] = useState('');
  const [shoppingBudget, setShoppingBudget] = useState('50');
  const [shoppingFeedLabel, setShoppingFeedLabel] = useState('PL');
  const [shoppingCreating, setShoppingCreating] = useState(false);
  // Step 1: URL input
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [urlGroups, setUrlGroups] = useState([{ label: '', url: '' }]);
  // Step 2: reach. `cities` is the list of selected geo targets, each:
  // { name, geo_id, radius_km, supports_radius, negative }
  const [reach, setReach] = useState('');
  const [cities, setCities] = useState([]);
  const [citySelectValue, setCitySelectValue] = useState(null);
  const [cityList, setCityList] = useState([]);        // raw suggestions from Google
  const [citySearch, setCitySearch] = useState('');
  const cityTimer = useRef(null);

  const fetchCities = (q) => {
    if (cityTimer.current) clearTimeout(cityTimer.current);
    if (!q || q.trim().length < 2) { setCityList([]); return; }
    cityTimer.current = setTimeout(async () => {
      try {
        const res = await api.get(`/ads/geo/cities/?q=${encodeURIComponent(q.trim())}`);
        setCityList(res.data?.cities || []);
      } catch { setCityList([]); }
    }, 300);
  };

  const addCity = (opt) => {
    if (!opt) return;
    const key = opt.geo_id || `c:${opt.name}`;
    setCities(prev => prev.some(c => (c.geo_id || `c:${c.name}`) === key) ? prev : [...prev, {
      name: opt.name,
      geo_id: opt.geo_id || '',
      supports_radius: !!opt.supports_radius,
      radius_km: opt.supports_radius ? '20' : '',
      negative: false,
    }]);
    setCitySelectValue(null);
    setCitySearch('');
    setCityList([]);
  };
  const removeCity = (i) => setCities(prev => prev.filter((_, idx) => idx !== i));
  const updateCity = (i, patch) => setCities(prev => prev.map((c, idx) => idx === i ? { ...c, ...patch } : c));
  const [countries, setCountries] = useState([]);
  const addCountryGroup = (codes) => setCountries(prev => Array.from(new Set([...prev, ...codes])));
  // Step 3: additional options
  const [maxCpc, setMaxCpc] = useState('');
  const [notes, setNotes] = useState('');
  // Target Google Ads account to publish to (when several are connected).
  const [googleAccounts, setGoogleAccounts] = useState([]);
  const [publishAccountId, setPublishAccountId] = useState('');
  // Processing
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [draftId, setDraftId] = useState(null);
  const [draftReady, setDraftReady] = useState(false);
  const [draftCampaign, setDraftCampaign] = useState(null);
  const [activeStep, setActiveStep] = useState(0);
  const [wizardStep, setWizardStep] = useState(0); // 0=type, 1=urls, 2=reach, 3=options, 4=processing
  const pollRef = useRef(null);
  const submittedRef = useRef(false);

  // Determine connected platforms. Google requires a concrete connected account
  // (customer_id), not just an OAuth token, otherwise a campaign can't actually
  // be published.
  const connectedPlatforms = [];
  if (user?.google_ads_customer_id) {
    connectedPlatforms.push({ id: 'google_ads', label: 'Google Ads', icon: 'G', color: 'blue' });
  }
  const metaAccounts = user?.organization?.platform_accounts?.filter(pa => pa.platform === 'meta_ads' && pa.is_active) || [];
  if (metaAccounts.length > 0) {
    connectedPlatforms.push({ id: 'meta_ads', label: 'Meta Ads', desc: 'Facebook + Instagram', icon: 'M', color: 'indigo' });
  }
  const isMetaCampaign = platform === 'meta_ads';

  const mountedRef = useRef(true);
  useEffect(() => {
    requestAnimationFrame(() => setVisible(true));
    return () => {
      mountedRef.current = false;
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  // Load connected Google Ads accounts so the user can choose where to publish
  // when several are connected (or "Wszystkie konta" is selected).
  useEffect(() => {
    if (!(user?.google_ads_customer_id || user?.google_ads_connected)) return;
    getGoogleAccounts()
      .then(res => {
        const gs = (res.data.accounts || []).filter(a => (a.platform || 'google_ads') === 'google_ads');
        setGoogleAccounts(gs);
        const cur = res.data.current_google_account_id;
        // Default: the active account if it's a concrete one, else the first.
        const def = (cur && cur !== '__all__') ? cur : (gs[0]?.id || '');
        setPublishAccountId(def);
      })
      .catch(() => {});
  }, [user]);

  const handleClose = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    setVisible(false);
    setTimeout(onClose, 300);
  };

  const addUrlGroup = () => setUrlGroups(prev => [...prev, { label: '', url: '' }]);
  const removeUrlGroup = (i) => setUrlGroups(prev => prev.filter((_, idx) => idx !== i));
  const updateUrlGroup = (i, field, value) => setUrlGroups(prev => prev.map((g, idx) => idx === i ? { ...g, [field]: value } : g));

  const handleSubmit = async () => {
    if (submittedRef.current) return;
    submittedRef.current = true;
    setSubmitting(true);
    setError('');

    try {
      // Send the full geo list (cities + exclusions). Radius only travels for
      // cities that support it; exclusions never carry a radius.
      const geoCities = (reach === 'local' || cities.some(c => c.negative))
        ? cities.map(c => ({
            name: c.name,
            geo_id: c.geo_id,
            radius_km: (!c.negative && c.supports_radius && c.radius_km) ? Number(c.radius_km) : undefined,
            negative: !!c.negative,
          }))
        : [];
      const firstPos = cities.find(c => !c.negative);
      const payload = {
        reach,
        cities: geoCities,
        city: reach === 'local' && firstPos ? firstPos.name : '',
        city_geo_id: reach === 'local' && firstPos ? firstPos.geo_id : '',
        radius_km: reach === 'local' && firstPos && firstPos.supports_radius && firstPos.radius_km ? Number(firstPos.radius_km) : undefined,
        countries: reach === 'international' ? countries : [],
        publish_account_id: (!isMetaCampaign && publishAccountId) ? publishAccountId : undefined,
        max_cpc_limit: maxCpc || null,
        notes: notes.trim(),
        platform: platform || 'google_ads',
        campaign_goal: platform === 'meta_ads' ? campaignGoal : undefined,
      };

      if (campaignType === 'single' || campaignType === 'performance_max') {
        // PMax uses one URL (Google rozszerza zasięg z zasobów), jak 'single'.
        payload.url = websiteUrl.trim();
        if (campaignType === 'performance_max') payload.campaign_type = 'performance_max';
      } else {
        // Multi-URL or referral: send URL groups
        const groups = urlGroups.filter(g => g.url.trim());
        if (campaignType === 'referral' && websiteUrl.trim()) {
          // Use the user's website as context URL for AI analysis
          payload.url = websiteUrl.trim();
          payload.context_url = websiteUrl.trim();
        } else {
          payload.url = groups[0]?.url.trim() || '';
        }
        payload.url_groups = groups.map(g => ({
          label: g.label.trim(),
          url: g.url.trim(),
        }));
        payload.campaign_type = campaignType;
      }

      const res = await api.post('/ads/drafts/start/', payload);
      const id = res.data.campaign_id || res.data.campaign?.id;
      setDraftId(id);
      if (onCreated) onCreated();
      setSubmitted(true);
      setWizardStep(4);
      setSubmitting(false);

      let stepIndex = 0;
      pollRef.current = setInterval(async () => {
        try {
          const statusRes = await api.get(`/ads/drafts/${id}/status/`);
          if (stepIndex < 3) {
            stepIndex++;
            setActiveStep(stepIndex);
          }
          if (statusRes.data.draft_status === 'ready') {
            clearInterval(pollRef.current);
            if (!mountedRef.current) return;
            setActiveStep(4);
            setDraftCampaign(statusRes.data);
            setTimeout(() => { if (mountedRef.current) setDraftReady(true); }, 800);
          } else if (statusRes.data.draft_status === 'failed') {
            clearInterval(pollRef.current);
            if (!mountedRef.current) return;
            setError(statusRes.data.ai_draft_data?.error || t('campaignWizard.errors.analysisFailed'));
            setSubmitted(false);
            setWizardStep(3);
            submittedRef.current = false;
          }
        } catch {}
      }, 3000);
    } catch (err) {
      const msg = err.response?.data?.error || t('campaignWizard.errors.draftFailed');
      setError(msg);
      setSubmitting(false);
      submittedRef.current = false;
    }
  };

  // Load Merchant Centers when Shopping is selected. Must run BEFORE any early
  // return so the hook order is identical across renders (avoids React #300).
  useEffect(() => {
    if (campaignType === 'shopping' && merchantCenters.length === 0) {
      api.get('/ads/merchant-centers/').then(res => {
        setMerchantCenters(res.data.merchants || []);
      }).catch(() => {});
    }
  }, [campaignType]); // eslint-disable-line

  if (draftReady && draftCampaign) {
    return <CampaignDraftReview campaign={draftCampaign} onClose={handleClose} onUpdated={onCreated} />;
  }

  const steps = isMetaCampaign ? [
    { label: t('campaignWizard.steps.analysis.label'), desc: t('campaignWizard.steps.analysis.desc') },
    { label: t('campaignWizard.steps.audienceMeta.label'), desc: t('campaignWizard.steps.audienceMeta.desc') },
    { label: t('campaignWizard.steps.adsMeta.label'), desc: t('campaignWizard.steps.adsMeta.desc') },
    { label: t('campaignWizard.steps.budget.label'), desc: t('campaignWizard.steps.budget.desc') },
  ] : [
    { label: t('campaignWizard.steps.analysis.label'), desc: t('campaignWizard.steps.analysis.desc') },
    { label: t('campaignWizard.steps.keywords.label'), desc: t('campaignWizard.steps.keywords.desc') },
    { label: t('campaignWizard.steps.ads.label'), desc: t('campaignWizard.steps.ads.desc') },
    { label: t('campaignWizard.steps.budget.label'), desc: t('campaignWizard.steps.budget.desc') },
  ];

  const handleCreateShopping = async () => {
    setShoppingCreating(true);
    setError('');
    try {
      // Create Shopping campaign via dedicated endpoint
      const res = await api.post('/ads/create-campaign/', {
        campaign_type: 'shopping',
        name: shoppingName.trim() || t('campaignWizard.step1.shopping.defaultName'),
        budget: parseFloat(shoppingBudget) || 50,
        merchant_id: selectedMerchant,
        feed_label: shoppingFeedLabel,
        platform: 'google_ads',
      });
      if (onCreated) onCreated();
      setSubmitted(true);
      setActiveStep(4);
      setTimeout(() => handleClose(), 1500);
    } catch (err) {
      setError(err.response?.data?.error || t('campaignWizard.errors.shoppingFailed'));
    }
    setShoppingCreating(false);
  };

  const canProceedFromType = !!campaignType && !!platform;
  const canProceedFromUrls = campaignType === 'shopping'
    ? !!selectedMerchant && !!shoppingName.trim()
    : (campaignType === 'single' || campaignType === 'performance_max')
      ? !!websiteUrl.trim()
      : urlGroups.some(g => g.url.trim());
  const canProceedFromReach = !!reach
    && (reach !== 'local' || cities.some(c => !c.negative))
    && (reach !== 'international' || countries.length > 0);

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div
        className={`absolute inset-0 bg-black/20 transition-opacity duration-300 ${visible ? 'opacity-100' : 'opacity-0'}`}
        onClick={handleClose}
      />
      <div
        className={`relative w-full md:w-[520px] bg-white h-full shadow-[-8px_0_30px_rgba(0,0,0,0.15)] transition-transform duration-300 flex flex-col ${
          visible ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 h-[57px] flex items-center justify-between z-10">
          <Group gap="sm">
            <ThemeIcon size={32} radius="md" variant="light" color={submitted && activeStep >= 4 ? 'green' : 'brand'}>
              {submitted ? (
                activeStep >= 4 ? <CheckCircle size={16} /> : <CircleNotch size={16} className="animate-spin" />
              ) : (
                <Sparkle size={16} />
              )}
            </ThemeIcon>
            <div>
              <Text size="sm" fw={600}>
                {submitted ? (activeStep >= 4 ? t('campaignWizard.header.ready') : t('campaignWizard.header.working')) : t('campaignWizard.header.newCampaign')}
              </Text>
              <Text size="xs" c="dimmed">
                {submitted ? t('campaignWizard.header.analysisInProgress') : t('campaignWizard.header.stepCounter', { current: wizardStep + 1 })}
              </Text>
            </div>
          </Group>
          <ActionIcon variant="subtle" color="gray" onClick={handleClose}>
            <X size={18} />
          </ActionIcon>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-6">

          {/* Step 0: Platform + Campaign type */}
          {wizardStep === 0 && (
            <div>
              <Title order={4} mb={4}>{t('campaignWizard.step0.platformTitle')}</Title>
              <Text size="sm" c="dimmed" mb="md">{t('campaignWizard.step0.platformSubtitle')}</Text>
              <div style={{ display: 'flex', gap: 10, marginBottom: 24 }}>
                {[
                  { id: 'google_ads', label: 'Google Ads', svg: '<svg viewBox="0 0 24 24" width="24" height="24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18A10.96 10.96 0 0 0 1 12c0 1.77.42 3.45 1.18 4.93l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>' },
                  { id: 'meta_ads', label: 'Meta Ads', svg: '<svg viewBox="0 0 24 24" width="24" height="24"><path fill="#1877F2" d="M24 12.073C24 5.405 18.627 0 12 0S0 5.405 0 12.073C0 18.1 4.388 23.094 10.125 24v-8.437H7.078v-3.49h3.047V9.41c0-3.026 1.792-4.697 4.533-4.697 1.312 0 2.686.236 2.686.236v2.971H15.83c-1.491 0-1.956.93-1.956 1.886v2.267h3.328l-.532 3.49h-2.796V24C19.612 23.094 24 18.1 24 12.073z"/></svg>' },
                ].map(p => {
                  const isAvailable = connectedPlatforms.some(cp => cp.id === p.id);
                  const selected = platform === p.id;
                  return (
                    <div
                      key={p.id}
                      onClick={() => isAvailable && (() => { setPlatform(p.id); setCampaignType(''); })()}
                      style={{
                        position: 'relative',
                        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                        gap: 6, padding: '16px 16px 20px', borderRadius: 12,
                        width: 100, height: 100,
                        border: `1.5px solid ${selected ? 'var(--mantine-color-brand-4)' : 'var(--mantine-color-gray-2)'}`,
                        backgroundColor: selected ? 'var(--mantine-color-brand-0)' : undefined,
                        cursor: isAvailable ? 'pointer' : 'default',
                        opacity: isAvailable ? 1 : 0.3,
                        transition: 'all 0.15s ease',
                      }}
                    >
                      <span dangerouslySetInnerHTML={{ __html: p.svg }} />
                      <Text size="xs" fw={600} c={selected ? 'brand.7' : 'gray.5'} ta="center" lh={1.2}>{p.label}</Text>
                      <span style={{
                        position: 'absolute', bottom: -10, left: '50%', transform: 'translateX(-50%)',
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        width: 22, height: 22, borderRadius: '50%',
                        backgroundColor: selected ? 'var(--mantine-color-brand-6)' : 'white',
                        border: selected ? 'none' : '2px solid var(--mantine-color-gray-3)',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                      }}>
                        {selected && <Check size={13} color="white" weight="bold" />}
                      </span>
                    </div>
                  );
                })}
              </div>

              {connectedPlatforms.length === 0 && (
                <Alert color="yellow" variant="light" radius="md" mb="md">
                  <Text size="sm">{t('campaignWizard.step0.noPlatform')}</Text>
                </Alert>
              )}

              <Title order={4} mb={4}>{t('campaignWizard.step0.typeTitle')}</Title>
              <Text size="sm" c="dimmed" mb="lg">
                {t('campaignWizard.step0.typeSubtitle')}
              </Text>
              <Stack gap="xs">
                {[
                  {
                    id: 'single',
                    label: isMetaCampaign ? t('campaignWizard.types.single.labelMeta') : t('campaignWizard.types.single.labelGoogle'),
                    desc: isMetaCampaign
                      ? t('campaignWizard.types.single.descMeta')
                      : t('campaignWizard.types.single.descGoogle'),
                    droid: isMetaCampaign ? null : t('campaignWizard.types.single.droid'),
                    icon: Globe,
                  },
                  {
                    id: 'multi',
                    label: isMetaCampaign ? t('campaignWizard.types.multi.labelMeta') : t('campaignWizard.types.multi.labelGoogle'),
                    desc: isMetaCampaign
                      ? t('campaignWizard.types.multi.descMeta')
                      : t('campaignWizard.types.multi.descGoogle'),
                    droid: isMetaCampaign ? null : t('campaignWizard.types.multi.droid'),
                    icon: Plus,
                  },
                  {
                    id: 'referral',
                    label: t('campaignWizard.types.referral.label'),
                    desc: t('campaignWizard.types.referral.desc'),
                    icon: LinkIcon,
                  },
                  // Shopping/PLA: the Google Merchant Center (content) OAuth scope
                  // was verified by Google on 2026-06-05, so this is enabled again.
                  ...(platform === 'google_ads' ? [{
                    id: 'shopping',
                    label: t('campaignWizard.types.shopping.label'),
                    desc: t('campaignWizard.types.shopping.desc'),
                    droid: t('campaignWizard.types.shopping.droid'),
                    icon: ShoppingCart,
                  }, {
                    id: 'performance_max',
                    label: t('campaignWizard.types.performanceMax.label'),
                    desc: t('campaignWizard.types.performanceMax.desc'),
                    droid: t('campaignWizard.types.performanceMax.droid'),
                    icon: Sparkle,
                  }] : []),
                ].map(opt => {
                  const Icon = opt.icon;
                  return (
                    <Paper
                      key={opt.id}
                      withBorder
                      p="md"
                      radius="md"
                      onClick={() => setCampaignType(opt.id)}
                      style={{
                        cursor: 'pointer',
                        borderColor: campaignType === opt.id ? 'var(--mantine-color-brand-3)' : undefined,
                        backgroundColor: campaignType === opt.id ? 'var(--mantine-color-brand-0)' : undefined,
                      }}
                    >
                      <Group gap="sm" wrap="nowrap">
                        <ThemeIcon size={36} radius="md" variant="light" color={campaignType === opt.id ? 'brand' : 'gray'}>
                          <Icon size={18} />
                        </ThemeIcon>
                        <div>
                          <Text size="sm" fw={600}>{opt.label}</Text>
                          <Text size="xs" c="dimmed" lh={1.4}>{opt.desc}</Text>
                          {opt.droid && (
                            <Group gap={5} wrap="nowrap" mt={6} align="flex-start">
                              <Sparkle size={13} weight="fill" style={{ color: 'var(--mantine-color-brand-5)', marginTop: 2, flexShrink: 0 }} />
                              <Text size="xs" lh={1.4} style={{ color: 'var(--mantine-color-brand-7)' }}>{opt.droid}</Text>
                            </Group>
                          )}
                        </div>
                      </Group>
                    </Paper>
                  );
                })}
              </Stack>

              {/* Campaign goal for Meta Ads */}
              {platform === 'meta_ads' && campaignType && (
                <>
                  <Title order={4} mb={4} mt="lg">{t('campaignWizard.step0.goalTitle')}</Title>
                  <Text size="sm" c="dimmed" mb="sm">{t('campaignWizard.step0.goalSubtitle')}</Text>
                  <div style={{ display: 'flex', gap: 8 }}>
                    {[
                      { id: 'traffic', label: t('campaignWizard.goals.traffic.label'), desc: t('campaignWizard.goals.traffic.desc'), icon: '🌐' },
                      { id: 'leads', label: t('campaignWizard.goals.leads.label'), desc: t('campaignWizard.goals.leads.desc'), icon: '📋' },
                    ].map(g => (
                      <Paper
                        key={g.id} withBorder p="sm" radius="md" style={{ flex: 1, cursor: 'pointer',
                          borderColor: campaignGoal === g.id ? 'var(--mantine-color-brand-3)' : undefined,
                          backgroundColor: campaignGoal === g.id ? 'var(--mantine-color-brand-0)' : undefined,
                        }}
                        onClick={() => setCampaignGoal(g.id)}
                      >
                        <Text size="lg" mb={2}>{g.icon}</Text>
                        <Text size="sm" fw={600}>{g.label}</Text>
                        <Text size="xs" c="dimmed">{g.desc}</Text>
                      </Paper>
                    ))}
                  </div>
                </>
              )}

              <Button color="brand" fullWidth mt="lg" disabled={!canProceedFromType} onClick={() => setWizardStep(1)}>
                {t('campaignWizard.common.next')}
              </Button>
            </div>
          )}

          {/* Step 1: URL input */}
          {wizardStep === 1 && (
            <div>
              {campaignType === 'shopping' ? (
                <>
                  <Title order={4} mb={4}>{t('campaignWizard.step1.shopping.title')}</Title>
                  <Text size="sm" c="dimmed" mb="md">{t('campaignWizard.step1.shopping.subtitle')}</Text>

                  <TextInput
                    label={t('campaignWizard.step1.shopping.nameLabel')}
                    placeholder={t('campaignWizard.step1.shopping.namePlaceholder')}
                    value={shoppingName}
                    onChange={e => setShoppingName(e.target.value)}
                    mb="sm"
                  />

                  {merchantCenters.length > 0 && (
                    <div className="mb-3">
                      <Text size="sm" fw={500} mb={4}>{t('campaignWizard.step1.shopping.merchantCenterHeading')}</Text>
                      <Stack gap="xs">
                        {merchantCenters.map(mc => (
                          <Paper
                            key={mc.merchant_id}
                            withBorder p="sm" radius="md"
                            onClick={() => setSelectedMerchant(mc.merchant_id)}
                            style={{
                              cursor: 'pointer',
                              borderColor: selectedMerchant === mc.merchant_id ? 'var(--mantine-color-brand-3)' : undefined,
                              backgroundColor: selectedMerchant === mc.merchant_id ? 'var(--mantine-color-brand-0)' : undefined,
                            }}
                          >
                            <Group justify="space-between">
                              <div>
                                <Group gap={6}>
                                  <Text size="sm" fw={500}>{mc.name || `MC ${mc.merchant_id}`}</Text>
                                  {mc.linked === false && (
                                    <Badge size="xs" color="orange" variant="light">{t('campaignWizard.step1.shopping.notLinked')}</Badge>
                                  )}
                                </Group>
                                <Text size="xs" c="dimmed">{t('campaignWizard.step1.shopping.mcId', { id: mc.merchant_id })}{mc.total_products ? t('campaignWizard.step1.shopping.mcProductsSuffix', { count: mc.total_products }) : ''}</Text>
                              </div>
                              {selectedMerchant === mc.merchant_id && <CheckCircle size={18} className="text-indigo-500" />}
                            </Group>
                          </Paper>
                        ))}
                      </Stack>
                    </div>
                  )}
                  {/* Zawsze pozwól wpisać ID Merchant Center ręcznie - nie wszystkie
                      konta są zsynchronizowane (np. brak zakresu Merchant Center),
                      a użytkownik może chcieć inne konto niż podpowiedziane. */}
                  <div className="mb-3">
                    <Text size="sm" fw={500} mb={4}>
                      {merchantCenters.length > 0 ? t('campaignWizard.step1.shopping.orEnterOtherMcId') : t('campaignWizard.step1.shopping.mcIdLabel')}
                    </Text>
                    <Text size="xs" c="dimmed" mb="xs">
                      {t('campaignWizard.step1.shopping.mcIdDesc')}
                    </Text>
                    <TextInput
                      placeholder={t('campaignWizard.step1.shopping.mcIdPlaceholder')}
                      value={selectedMerchant}
                      onChange={e => setSelectedMerchant(e.target.value.replace(/\D/g, ''))}
                      type="text"
                      inputMode="numeric"
                    />
                  </div>

                  <Group grow gap="sm">
                    <TextInput
                      label={t('campaignWizard.step1.shopping.budgetLabel')}
                      value={shoppingBudget}
                      onChange={e => setShoppingBudget(e.target.value)}
                      type="number"
                      min={10}
                    />
                    <TextInput
                      label={t('campaignWizard.step1.shopping.feedLabelLabel')}
                      value={shoppingFeedLabel}
                      onChange={e => setShoppingFeedLabel(e.target.value)}
                      placeholder="PL"
                    />
                  </Group>

                  {error && (
                    <Alert color="red" mt="sm" radius="md">
                      <Text size="sm">{error}</Text>
                    </Alert>
                  )}
                </>
              ) : (campaignType === 'single' || campaignType === 'performance_max') ? (
                <>
                  <Title order={4} mb={4}>{t('campaignWizard.step1.single.title')}</Title>
                  <Text size="sm" c="dimmed" mb="lg">
                    {campaignType === 'performance_max'
                      ? t('campaignWizard.step1.single.descPmax')
                      : t('campaignWizard.step1.single.desc')}
                  </Text>
                  <TextInput
                    label={t('campaignWizard.step1.single.urlLabel')}
                    leftSection={<Globe size={16} />}
                    value={websiteUrl}
                    onChange={e => setWebsiteUrl(e.target.value)}
                    placeholder={t('campaignWizard.step1.single.urlPlaceholder')}
                    autoFocus
                    size="md"
                  />
                </>
              ) : (
                <>
                  <Title order={4} mb={4}>
                    {campaignType === 'referral' ? t('campaignWizard.step1.multi.titleReferral') : t('campaignWizard.step1.multi.title')}
                  </Title>
                  <Text size="sm" c="dimmed" mb="lg">
                    {campaignType === 'referral'
                      ? t('campaignWizard.step1.multi.descReferral')
                      : t('campaignWizard.step1.multi.desc')}
                  </Text>

                  {campaignType === 'referral' && (
                    <TextInput
                      label={t('campaignWizard.step1.multi.websiteLabel')}
                      description={isMetaCampaign ? t('campaignWizard.step1.multi.websiteDescMeta') : t('campaignWizard.step1.multi.websiteDescGoogle')}
                      leftSection={<Globe size={16} />}
                      value={websiteUrl}
                      onChange={e => setWebsiteUrl(e.target.value)}
                      placeholder={t('campaignWizard.step1.single.urlPlaceholder')}
                      size="sm"
                      mb="md"
                    />
                  )}

                  <Text size="xs" fw={600} c="dimmed" mb="xs" style={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    {campaignType === 'referral' ? t('campaignWizard.step1.multi.sectionReferral') : t('campaignWizard.step1.multi.section')}
                  </Text>
                  <Stack gap="sm">
                    {urlGroups.map((g, i) => (
                      <Paper key={i} withBorder p="sm" radius="md">
                        <Group justify="space-between" mb="xs">
                          <Text size="xs" fw={600} c="dimmed">{t('campaignWizard.step1.multi.productHeading', { n: i + 1 })}</Text>
                          {urlGroups.length > 1 && (
                            <ActionIcon variant="subtle" color="red" size="sm" onClick={() => removeUrlGroup(i)}>
                              <Trash size={14} />
                            </ActionIcon>
                          )}
                        </Group>
                        <Stack gap="xs">
                          <TextInput
                            placeholder={t('campaignWizard.step1.multi.namePlaceholder')}
                            value={g.label}
                            onChange={e => updateUrlGroup(i, 'label', e.target.value)}
                            size="sm"
                          />
                          <TextInput
                            placeholder={campaignType === 'referral' ? t('campaignWizard.step1.multi.urlPlaceholderReferral') : t('campaignWizard.step1.multi.urlPlaceholder')}
                            value={g.url}
                            onChange={e => updateUrlGroup(i, 'url', e.target.value)}
                            size="sm"
                            leftSection={<LinkIcon size={14} />}
                          />
                        </Stack>
                      </Paper>
                    ))}
                    <Button variant="light" color="brand" size="sm" leftSection={<Plus size={14} />} onClick={addUrlGroup}>
                      {t('campaignWizard.step1.multi.addAnother')}
                    </Button>
                  </Stack>
                </>
              )}

              <Group mt="lg">
                <Button variant="default" onClick={() => setWizardStep(0)}>{t('campaignWizard.common.back')}</Button>
                {campaignType === 'shopping' ? (
                  <Button
                    color="brand" style={{ flex: 1 }}
                    disabled={!selectedMerchant || !shoppingName.trim()}
                    onClick={handleCreateShopping}
                    loading={shoppingCreating}
                  >
                    {t('campaignWizard.step1.shopping.createButton')}
                  </Button>
                ) : (
                  <Button color="brand" style={{ flex: 1 }} disabled={!canProceedFromUrls} onClick={() => setWizardStep(2)}>
                    {t('campaignWizard.common.next')}
                  </Button>
                )}
              </Group>
            </div>
          )}

          {/* Step 2: Reach */}
          {wizardStep === 2 && (
            <div>
              <Title order={4} mb={4}>{t('campaignWizard.step2.title')}</Title>
              <Text size="sm" c="dimmed" mb="lg">{isMetaCampaign ? t('campaignWizard.step2.subtitleMeta') : t('campaignWizard.step2.subtitle')}</Text>
              <Stack gap="xs">
                {[
                  { id: 'local', label: t('campaignWizard.step2.reach.local.label'), desc: t('campaignWizard.step2.reach.local.desc') },
                  { id: 'national', label: t('campaignWizard.step2.reach.national.label'), desc: t('campaignWizard.step2.reach.national.desc') },
                  { id: 'international', label: t('campaignWizard.step2.reach.international.label'), desc: t('campaignWizard.step2.reach.international.desc') },
                ]
                  // Meta Ads (our builder) targets the whole country, so the
                  // city/radius "Lokalnie" option doesn't apply - hide it.
                  .filter(opt => !(isMetaCampaign && opt.id === 'local'))
                  .map(opt => (
                  <Paper
                    key={opt.id}
                    withBorder p="sm" radius="md"
                    onClick={() => setReach(opt.id)}
                    style={{
                      cursor: 'pointer',
                      borderColor: reach === opt.id ? 'var(--mantine-color-brand-3)' : undefined,
                      backgroundColor: reach === opt.id ? 'var(--mantine-color-brand-0)' : undefined,
                    }}
                  >
                    <Text size="sm" fw={500} c={reach === opt.id ? 'brand.7' : 'dark'}>{opt.label}</Text>
                    <Text size="xs" c="dimmed">{opt.desc}</Text>
                  </Paper>
                ))}
              </Stack>
              {reach === 'local' && (
                <Box mt="md">
                  <Text size="sm" fw={500} mb={6}>{t('campaignWizard.step2.citiesLabel')}</Text>
                  <Select
                    placeholder={t('campaignWizard.step2.citiesPlaceholder')}
                    searchable
                    clearable
                    value={citySelectValue}
                    searchValue={citySearch}
                    onSearchChange={(v) => { setCitySearch(v); fetchCities(v); }}
                    data={cityList.map(c => ({ value: c.geo_id || `c:${c.name}`, label: c.display || c.name }))}
                    filter={({ options }) => options}
                    nothingFoundMessage={citySearch.trim().length >= 2 ? t('campaignWizard.step2.noCities') : t('campaignWizard.step2.minChars')}
                    onChange={(val) => addCity(cityList.find(c => (c.geo_id || `c:${c.name}`) === val))}
                    description={t('campaignWizard.step2.citiesDesc')}
                  />
                  {cities.length > 0 && (
                    <Stack gap={6} mt="sm">
                      {cities.map((c, i) => (
                        <Paper key={c.geo_id || `c:${c.name}`} withBorder p="xs" radius="md">
                          <Group justify="space-between" wrap="nowrap" gap="xs">
                            <Text size="sm" fw={500} truncate style={{ flex: 1, minWidth: 0 }}
                              c={c.negative ? 'red.6' : 'dark'}>
                              {c.negative ? t('campaignWizard.step2.excludePrefix') : ''}{c.name}
                            </Text>
                            {!c.negative && c.supports_radius && (
                              <Select
                                size="xs" w={104}
                                value={c.radius_km}
                                onChange={(v) => updateCity(i, { radius_km: v })}
                                data={[
                                  { value: '5', label: '5 km' },
                                  { value: '10', label: '10 km' },
                                  { value: '20', label: '20 km' },
                                  { value: '30', label: '30 km' },
                                  { value: '50', label: '50 km' },
                                ]}
                              />
                            )}
                            {!c.negative && !c.supports_radius && (
                              <Text size="10px" c="dimmed">{t('campaignWizard.step2.wholeCity')}</Text>
                            )}
                            <Button size="compact-xs"
                              variant={c.negative ? 'filled' : 'light'}
                              color={c.negative ? 'red' : 'gray'}
                              onClick={() => updateCity(i, { negative: !c.negative })}>
                              {c.negative ? t('campaignWizard.step2.excluded') : t('campaignWizard.step2.exclude')}
                            </Button>
                            <ActionIcon size="sm" variant="subtle" color="gray" onClick={() => removeCity(i)}>×</ActionIcon>
                          </Group>
                        </Paper>
                      ))}
                    </Stack>
                  )}
                  <Text size="10px" c="dimmed" mt={6}>
                    {t('campaignWizard.step2.radiusNote')}
                  </Text>
                </Box>
              )}
              {reach === 'international' && (
                <Box mt="md">
                  <Text size="sm" fw={500} mb={6}>{t('campaignWizard.step2.countriesLabel')}</Text>
                  <Group gap={6} mb="xs">
                    <Button size="compact-xs" variant="light" color="brand" onClick={() => addCountryGroup(COUNTRY_GROUPS.POPULAR)}>{t('campaignWizard.step2.groupPopular')}</Button>
                    <Button size="compact-xs" variant="light" color="brand" onClick={() => addCountryGroup(COUNTRY_GROUPS.EU)}>{t('campaignWizard.step2.groupEu')}</Button>
                    <Button size="compact-xs" variant="light" color="brand" onClick={() => addCountryGroup(COUNTRY_GROUPS.EUROPE)}>{t('campaignWizard.step2.groupEurope')}</Button>
                    {countries.length > 0 && (
                      <Button size="compact-xs" variant="subtle" color="gray" onClick={() => setCountries([])}>{t('campaignWizard.step2.clear')}</Button>
                    )}
                  </Group>
                  <MultiSelect
                    data={COUNTRY_OPTIONS}
                    value={countries}
                    onChange={setCountries}
                    placeholder={countries.length ? '' : t('campaignWizard.step2.countriesPlaceholder')}
                    searchable
                    clearable
                    hidePickedOptions
                    classNames={{ input: 'droid-multiselect', wrapper: 'insights-multiselect-wrapper' }}
                    maxDropdownHeight={240}
                    description={t('campaignWizard.step2.countriesDesc')}
                    nothingFoundMessage={t('campaignWizard.step2.noCountry')}
                  />
                </Box>
              )}
              <Group mt="lg">
                <Button variant="default" onClick={() => setWizardStep(1)}>{t('campaignWizard.common.back')}</Button>
                <Button color="brand" style={{ flex: 1 }} disabled={!canProceedFromReach} onClick={() => setWizardStep(3)}>
                  {t('campaignWizard.common.next')}
                </Button>
              </Group>
            </div>
          )}

          {/* Step 3: Additional options */}
          {wizardStep === 3 && (
            <div>
              <Title order={4} mb={4}>{t('campaignWizard.step3.title')}</Title>
              <Text size="sm" c="dimmed" mb="lg">{t('campaignWizard.step3.subtitle')}</Text>

              <Stack gap="md">
                {!isMetaCampaign && googleAccounts.length > 1 && (
                  <Select
                    label={t('campaignWizard.step3.accountLabel')}
                    description={t('campaignWizard.step3.accountDesc')}
                    data={googleAccounts.map(a => ({ value: a.id, label: `${a.custom_name || a.name || t('campaignWizard.step3.accountFallback', { id: a.id })} (${a.id})` }))}
                    value={publishAccountId}
                    onChange={(v) => setPublishAccountId(v || '')}
                    searchable
                    allowDeselect={false}
                    size="sm"
                  />
                )}
                {!isMetaCampaign && (
                  <TextInput
                    label={t('campaignWizard.step3.cpcLabel')}
                    description={t('campaignWizard.step3.cpcDesc')}
                    type="number"
                    step="0.5"
                    min="0"
                    placeholder={t('campaignWizard.step3.cpcPlaceholder')}
                    value={maxCpc}
                    onChange={e => setMaxCpc(e.target.value)}
                    size="sm"
                  />
                )}

                <Textarea
                  label={t('campaignWizard.step3.notesLabel')}
                  description={isMetaCampaign
                    ? t('campaignWizard.step3.notesDescMeta')
                    : t('campaignWizard.step3.notesDescGoogle')}
                  value={notes}
                  onChange={e => setNotes(e.target.value)}
                  rows={3}
                  placeholder={isMetaCampaign
                    ? t('campaignWizard.step3.notesPlaceholderMeta')
                    : t('campaignWizard.step3.notesPlaceholderGoogle')}
                  size="sm"
                />

                {campaignType === 'referral' && (
                  <Alert color="blue" variant="light" radius="md">
                    <Text size="xs" fw={500} mb={2}>{t('campaignWizard.step3.referralAlertTitle')}</Text>
                    <Text size="xs" c="dimmed" lh={1.5}>
                      {isMetaCampaign
                        ? t('campaignWizard.step3.referralAlertMeta')
                        : t('campaignWizard.step3.referralAlertGoogle')}
                    </Text>
                  </Alert>
                )}

                {error && <Text size="xs" c="red">{error}</Text>}
              </Stack>

              <Group mt="lg">
                <Button variant="default" onClick={() => setWizardStep(2)}>{t('campaignWizard.common.back')}</Button>
                <Button
                  color="brand"
                  style={{ flex: 1 }}
                  onClick={handleSubmit}
                  loading={submitting}
                  leftSection={!submitting && <Sparkle size={16} />}
                >
                  {submitting ? t('campaignWizard.step3.submitting') : t('campaignWizard.step3.submit')}
                </Button>
              </Group>
            </div>
          )}

          {/* Step 4: Processing */}
          {wizardStep === 4 && (
            <div>
              <Stack gap="md" mb="lg">
                {steps.map((s, i) => {
                  const isDone = i < activeStep;
                  const isActive = i === activeStep;
                  return (
                    <Group key={i} gap="sm" wrap="nowrap" align="flex-start">
                      <ThemeIcon
                        size={24} radius="xl" variant="light"
                        color={isDone ? 'green' : isActive ? 'brand' : 'gray'}
                        style={{ flexShrink: 0, marginTop: 2 }}
                      >
                        {isDone ? <CheckCircle size={14} /> : isActive ? <CircleNotch size={14} className="animate-spin" /> : <Box style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--mantine-color-gray-4)' }} />}
                      </ThemeIcon>
                      <div>
                        <Text size="sm" fw={isDone || isActive ? 500 : 400} c={isDone ? 'green.7' : isActive ? 'dark' : 'dimmed'}>{s.label}</Text>
                        <Text size="xs" c="dimmed">{s.desc}</Text>
                      </div>
                    </Group>
                  );
                })}
              </Stack>
              {activeStep >= 4 && (
                <Alert color="green" radius="md" style={{ textAlign: 'center' }}>
                  <Text size="sm" fw={500}>{t('campaignWizard.step4.doneTitle')}</Text>
                  <Text size="xs" c="dimmed">{t('campaignWizard.step4.doneText')}</Text>
                </Alert>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <Paper p="sm" bg="gray.0" style={{ borderTop: '1px solid var(--mantine-color-gray-3)' }}>
          <Group gap="xs" wrap="nowrap">
            <Info size={14} className="text-gray-400 flex-shrink-0" />
            <Text size="xs" c="dimmed">
              {submitted
                ? t('campaignWizard.footer.submitted')
                : t('campaignWizard.footer.default')}
            </Text>
          </Group>
        </Paper>
      </div>
    </div>
  );
}
